<template>
  <div v-if="isLoading">
    <SpinnerView />
  </div>
  <div v-else class="zones-page space-y-5">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Zones</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
          {{ data.length }} zone{{ data.length !== 1 ? 's' : '' }} configured
        </p>
      </div>
      <button
        @click="openCreateZone"
        class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        <span class="material-icons text-[18px]" aria-hidden="true">add</span>
        New Zone
      </button>
    </div>

    <!-- Stale-data refresh failure -->
    <AlertBanner
      v-if="fetchError && data.length"
      type="error"
      message="Couldn't refresh zones — showing the last loaded list."
      :dismissible="false"
      class="mb-4"
    />

    <!-- Fetch failure with nothing loaded -->
    <div v-if="fetchError && !data.length" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700">
      <ErrorState title="Couldn't load zones" :message="fetchError" @retry="fetchZones" />
    </div>

    <template v-else>
      <!-- Tab Switcher -->
      <div class="flex gap-1 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg w-fit" role="tablist" aria-label="Zone views">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="activeTab = tab.key"
          role="tab"
          :aria-selected="activeTab === tab.key"
          class="px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200"
          :class="activeTab === tab.key
            ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm'
            : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'"
        >
          <span class="material-icons text-[16px] align-middle mr-1" aria-hidden="true">{{ tab.icon }}</span>
          {{ tab.label }}
        </button>
      </div>

      <!-- Map View -->
      <div v-show="activeTab === 'map'" class="zones-map bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <GMapMap
          :zoom="11"
          :center="mapCenter"
          map-type-id="terrain"
          style="width: 100%; height: 500px"
          :options="{
            zoomControl: true,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
          }"
        >
          <div v-for="zone in data" :key="zone.zoneId || zone.id">
            <GMapPolygon
              :paths="zone.areas"
              :options="{
                fillColor: getZoneColor(zone),
                fillOpacity: 0.2,
                strokeColor: getZoneColor(zone),
                strokeWeight: 2,
                strokeOpacity: 0.8,
              }"
            />
            <GMapInfoWindow
              :options="{
                content: `<div style='font-weight:600;font-size:12px;padding:2px 4px;color:#171717'>${zone.zoneName}</div>`,
                position: getPolygonCenter(zone.areas),
                disableAutoPan: true,
              }"
            />
          </div>
        </GMapMap>
      </div>

      <!-- Schedule Grid -->
      <div v-show="activeTab === 'schedule'" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
          <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Operational Schedule</h2>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Zone</th>
                <th
                  v-for="day in weekdays"
                  :key="day.short"
                  class="px-3 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 w-20"
                >
                  <span class="hidden sm:inline">{{ day.full }}</span>
                  <span class="sm:hidden">{{ day.short }}</span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
              <tr
                v-for="zone in data"
                :key="zone.zoneId"
                class="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
              >
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ background: getZoneColor(zone) }"></span>
                    <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100">{{ zone.zoneName }}</span>
                  </div>
                </td>
                <td
                  v-for="day in weekdays"
                  :key="day.full"
                  class="px-3 py-3 text-center"
                >
                  <label class="inline-flex items-center justify-center cursor-pointer">
                    <input
                      type="checkbox"
                      :checked="zone.zoneOperationalDays?.[day.full]"
                      :aria-label="`${zone.zoneName} operational on ${day.full}`"
                      @change="updateOperationalDay(zone.zoneId, day.full, $event.target.checked)"
                      class="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 dark:bg-neutral-700 cursor-pointer"
                    />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!data.length" class="py-12 text-center">
            <span class="material-icons text-4xl text-neutral-300 dark:text-neutral-600" aria-hidden="true">map</span>
            <p class="text-neutral-500 dark:text-neutral-400 mt-2">No zones configured</p>
          </div>
        </div>
      </div>

      <!-- Zones List -->
      <div v-show="activeTab === 'list'" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
          <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Zone Details</h2>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th class="px-4 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 w-12">#</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Zone</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Description</th>
                <th class="px-4 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300">Bounds</th>
                <th class="px-4 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 w-20">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
              <tr
                v-for="(zone, idx) in data"
                :key="zone.zoneId"
                class="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
              >
                <td class="px-4 py-3 text-center text-sm text-neutral-500 dark:text-neutral-400">{{ idx + 1 }}</td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ background: getZoneColor(zone) }"></span>
                    <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100">{{ zone.zoneName }}</span>
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400">{{ zone.zoneDescription || '—' }}</td>
                <td class="px-4 py-3 text-center">
                  <span class="text-xs font-mono text-neutral-500 dark:text-neutral-400">{{ zone.areas?.length || 0 }} points</span>
                </td>
                <td class="px-4 py-3 text-center">
                  <router-link
                    :to="{ name: 'ZoneView', params: { id: zone.zoneId } }"
                    class="p-2 rounded-lg transition-all duration-150 text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 inline-flex"
                    title="Edit zone"
                    aria-label="Edit zone"
                  >
                    <span class="material-icons text-[18px]" aria-hidden="true">edit</span>
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!data.length" class="py-12 text-center">
            <span class="material-icons text-4xl text-neutral-300 dark:text-neutral-600" aria-hidden="true">map</span>
            <p class="text-neutral-500 dark:text-neutral-400 mt-2">No zones configured</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Create New Zone Modal -->
    <Modal :show="createNewZone" title="Create New Zone" size="lg" @close="createNewZone = false">
      <div class="space-y-4">
        <div>
          <label for="new-zone-name" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Zone Name</label>
          <input
            id="new-zone-name"
            class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            type="text"
            placeholder="e.g. Downtown Dubai"
            v-model="newZone.zoneName"
          />
        </div>
        <div>
          <label for="new-zone-description" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Description</label>
          <input
            id="new-zone-description"
            class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            type="text"
            placeholder="Optional description"
            v-model="newZone.zoneDescription"
          />
        </div>

        <div>
          <span class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">Geo Bounds</span>
          <div class="space-y-2">
            <div v-for="(area, index) in newZone.areas" :key="index" class="flex items-center gap-2">
              <span class="text-xs text-neutral-400 w-5 text-center flex-shrink-0">{{ index + 1 }}</span>
              <input
                class="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm tabular-nums"
                type="number"
                step="any"
                min="-90"
                max="90"
                placeholder="Latitude"
                :aria-label="`Point ${index + 1} latitude`"
                v-model="area.lat"
              />
              <input
                class="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm tabular-nums"
                type="number"
                step="any"
                min="-180"
                max="180"
                placeholder="Longitude"
                :aria-label="`Point ${index + 1} longitude`"
                v-model="area.lng"
              />
              <button
                @click="removeArea(index)"
                :aria-label="`Remove point ${index + 1}`"
                class="p-1.5 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-colors"
              >
                <span class="material-icons text-[16px]" aria-hidden="true">close</span>
              </button>
            </div>
          </div>
          <button
            @click="addArea"
            class="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
          >
            <span class="material-icons text-[16px]" aria-hidden="true">add</span>
            Add point
          </button>
        </div>

        <div v-if="createError" role="alert" class="flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
          <span class="material-icons text-red-500 text-sm" aria-hidden="true">error</span>
          <p class="text-xs text-red-700 dark:text-red-400">{{ createError }}</p>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button
            @click="createNewZone = false"
            class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="submitNewZone"
            :disabled="isSubmitting || !newZone.zoneName"
            class="px-4 py-2 text-sm font-semibold text-white bg-primary-700 rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSubmitting ? 'Creating...' : 'Create Zone' }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import SpinnerView from "@/components/SpinnerView.vue";
import Modal from "@/components/ui/Modal.vue";
import ErrorState from "@/components/ui/ErrorState.vue";
import AlertBanner from "@/components/ui/AlertBanner.vue";
import useToast from "@/composables/useToast";
import { useStore } from "vuex";
import { ref } from "vue";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase/config";

const ZONE_COLORS = [
  "#1E90FF", "#059669", "#d97706", "#dc2626", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
];

export default {
  components: { SpinnerView, Modal, ErrorState, AlertBanner },
  setup() {
    const store = useStore();
    const { showToast } = useToast();
    const isLoading = ref(true);
    const data = ref([]);
    const fetchError = ref(null);
    const isSubmitting = ref(false);
    const createNewZone = ref(false);
    const createError = ref(null);
    const activeTab = ref("schedule");

    const newZone = ref({
      zoneName: "",
      zoneDescription: "",
      areas: [],
    });

    const tabs = [
      { key: "schedule", label: "Schedule", icon: "calendar_today" },
      { key: "map", label: "Map", icon: "map" },
      { key: "list", label: "Zones", icon: "list" },
    ];

    const weekdays = [
      { full: "Sunday", short: "Sun" },
      { full: "Monday", short: "Mon" },
      { full: "Tuesday", short: "Tue" },
      { full: "Wednesday", short: "Wed" },
      { full: "Thursday", short: "Thu" },
      { full: "Friday", short: "Fri" },
      { full: "Saturday", short: "Sat" },
    ];

    const mapCenter = { lat: 25.150398, lng: 55.255297 };

    const getZoneColor = (zone) => {
      const idx = data.value.indexOf(zone);
      return ZONE_COLORS[idx % ZONE_COLORS.length];
    };

    const getPolygonCenter = (paths) => {
      if (!paths?.length) return mapCenter;
      let lat = 0, lng = 0;
      paths.forEach((p) => { lat += p.lat; lng += p.lng; });
      return { lat: lat / paths.length, lng: lng / paths.length };
    };

    // --- Data fetching ---

    const fetchZones = async () => {
      try {
        const apiUrl = `${store.state.apiUrl}/getZonePlannerData/${store.state.currentSite}`;
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        data.value = await response.json();
        fetchError.value = null;
      } catch (error) {
        console.error("Failed to fetch zones:", error);
        fetchError.value = error.message || "Request failed.";
      } finally {
        isLoading.value = false;
      }
    };

    // --- Schedule updates (Firebase) ---

    const updateOperationalDay = async (zoneId, weekday, value) => {
      // Optimistic update: keep the checkbox in sync locally and roll back
      // (with a toast) on failure — avoids a full-table refetch per toggle.
      const zone = data.value.find((z) => z.zoneId === zoneId);
      const previous = zone?.zoneOperationalDays?.[weekday] ?? false;
      if (zone) {
        if (!zone.zoneOperationalDays) zone.zoneOperationalDays = {};
        zone.zoneOperationalDays[weekday] = value;
      }
      try {
        const zoneDoc = doc(db, "zoneOperationalDays", zoneId);
        await updateDoc(zoneDoc, { [weekday]: value });
      } catch (error) {
        console.error("Failed to update operational day:", error);
        if (zone) zone.zoneOperationalDays[weekday] = previous;
        showToast("error", `Failed to update ${weekday} for ${zone?.zoneName || "zone"}. Please try again.`);
      }
    };

    // --- Create zone ---

    const openCreateZone = () => {
      createError.value = null;
      createNewZone.value = true;
    };

    const submitNewZone = async () => {
      if (isSubmitting.value) return;
      createError.value = null;

      const hasInvalidPoint = newZone.value.areas.some(
        (a) => a.lat === "" || a.lng === "" || a.lat === null || a.lng === null ||
          Number.isNaN(Number(a.lat)) || Number.isNaN(Number(a.lng))
      );
      if (hasInvalidPoint) {
        createError.value = "Every point needs both a latitude and a longitude.";
        return;
      }

      isSubmitting.value = true;
      try {
        const response = await fetch(`${store.state.apiUrl}/createZone`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            zoneName: newZone.value.zoneName,
            siteId: store.state.currentSite,
            zoneDescription: newZone.value.zoneDescription,
            areas: newZone.value.areas.map((a) => ({ lat: Number(a.lat), lng: Number(a.lng) })),
          }),
        });
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        // Only close + reset on success — a failure must keep the user's input.
        createNewZone.value = false;
        newZone.value = { zoneName: "", zoneDescription: "", areas: [] };
        showToast("success", "Zone created");
        await fetchZones();
      } catch (error) {
        console.error("Failed to create zone:", error);
        createError.value = "Could not create the zone. Your input has been kept — please try again.";
        showToast("error", "Failed to create zone. Please try again.");
      } finally {
        isSubmitting.value = false;
      }
    };

    const addArea = () => {
      // Seed from the previous point (or the site map center) — a 0,0 default
      // instantly stretches the preview polygon to the Gulf of Guinea.
      const last = newZone.value.areas[newZone.value.areas.length - 1];
      const seed = last && !Number.isNaN(Number(last.lat)) && !Number.isNaN(Number(last.lng)) && last.lat !== "" && last.lng !== ""
        ? { lat: Number(last.lat), lng: Number(last.lng) }
        : { ...mapCenter };
      newZone.value.areas.push(seed);
    };

    const removeArea = (index) => {
      newZone.value.areas.splice(index, 1);
    };

    fetchZones();
    store.commit("setCurrentRoute", "Zones");

    return {
      isLoading,
      data,
      fetchError,
      activeTab,
      tabs,
      weekdays,
      mapCenter,
      createNewZone,
      createError,
      newZone,
      isSubmitting,
      getZoneColor,
      getPolygonCenter,
      updateOperationalDay,
      openCreateZone,
      submitNewZone,
      addArea,
      removeArea,
      fetchZones,
    };
  },
};
</script>

<style>
/* Hide InfoWindow close button — scoped to this page's map so other
   Google Maps instances keep their dismiss affordance. */
.zones-map .gm-ui-hover-effect {
  display: none !important;
}
</style>

<style scoped>
.zones-page > * {
  animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.zones-page > *:nth-child(1) { animation-delay: 0s; }
.zones-page > *:nth-child(2) { animation-delay: 0.05s; }
.zones-page > *:nth-child(3) { animation-delay: 0.1s; }

@media (prefers-reduced-motion: reduce) {
  .zones-page > * {
    animation: none;
  }
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
